import { createSearchParams, useParams } from 'react-router-dom'
import { deleteRequest, getRequest } from '../../../services/base'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { NavContext } from '../../../context/global_context'
import {
  SET_SELECTED_VISUAL_AID_DATA,
  SET_VISUAL_AID_DATA,
  SHOW_HIDE_VISUAL_AID_FORM,
  VisualAidContext,
} from './context/context'
import InputText from '../../common/InputText'
import VisualAidTable from './VisualAidTable'
import VisualAidFormModal from './form'
import Button from '../../common/Button'
import { getSessionStorageData } from '../../../utils/SessionStorage'
import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Flex, Form, Modal, Row, Space } from 'antd'
import { buildPageUrl, downloadFile, downloadFileBlob, getDeleteTargetPage } from '../../../utils/common'
import useDebouncedValue from '../../../utils/common_function'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const VisualAidList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(VisualAidContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(
    `/api/visual_aid/?search=${search}&vehicle=${vehicle}`
  )

  const debouncedSearch = useDebouncedValue(search, 500)

  const [modal, contextHolder] = Modal.useModal()

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  const getVisualAidList = useCallback(
    async (url) => {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({ id: 'visualAid.fetchingData' }),
          showLoader: true,
        },
        notification: {},
      })

      currentUrl.current = url ? url : currentUrl.current
      let res = await getRequest(currentUrl.current)
      setPagination(res.pagination)
      dispatch({ type: SET_VISUAL_AID_DATA, data: res })

      navDispatch({
        type: 'loader',
        data: { showLoader: false },
        notification: {},
      })

      // After the loader dispatch above: a 'loader' action overwrites `notification`,
      // so notifying before it would be wiped out in the same React batch.
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'visualAid.loadError' }))
      }
    },
    [intl, navDispatch, dispatch]
  )

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.visualAid' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/visual_aid/?search=${search}&vehicle=${vehicle}`
    getVisualAidList()
  }, [debouncedSearch, getVisualAidList])

  const onEdit = useCallback(
    (record) => {
      dispatch({
        type: SET_SELECTED_VISUAL_AID_DATA,
        data: {
          ...record,
          index: record.index,
        },
      })
    },
    [dispatch]
  )

  const deleteVisualAid = useCallback(
    async (record) => {
      navDispatch({
        type: 'loader',
        data: {
          caption: intl.formatMessage({ id: 'visualAid.deleting' }),
          showLoader: true,
        },
        notification: {},
      })
      let res = await deleteRequest(
        `/api/visual_aid/${record.id}/?vehicle=${vehicle}`
      )
      navDispatch({
        type: 'loader',
        data: { showLoader: false },
        notification: {},
      })
      if (res?.status) {
        currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
        // Awaited before the toast: getVisualAidList() opens with a 'loader'
        // dispatch carrying notification: {}, which would wipe it - see the
        // same fix in form/index.js.
        await getVisualAidList()
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'success',
            msg: intl.formatMessage({ id: 'visualAid.deleteSuccess' }),
          },
        })
      } else {
        navDispatch({
          type: 'notification',
          data: {
            notification_type: 'error',
            msg: intl.formatMessage({ id: 'visualAid.deleteError' }),
          },
        })
      }
    },
    [intl, navDispatch, vehicle, getVisualAidList, pagination]
  )

  const confirmDelete = useCallback(
    (record) => {
      modal.confirm({
        title: intl.formatMessage({ id: 'visualAid.confirmDelete' }),
        icon: <ExclamationCircleOutlined />,
        content: intl.formatMessage(
          { id: 'visualAid.confirmDeleteContent' },
          { name: record.name }
        ),
        onOk: () => deleteVisualAid(record),
        centered: true,
        maskClosable: true,
      })
    },
    [modal, deleteVisualAid, intl]
  )

  const downloadVisualAidFile = useCallback(
    async (record) => {
      modal.confirm({
        title: intl.formatMessage({ id: 'visualAid.downloadConfirm' }),
        icon: <DownloadOutlined />,
        content: intl.formatMessage({ id: 'visualAid.downloadConfirmContent' }),
        centered: true,
        onOk: async () => {
          navDispatch({
            type: 'loader',
            data: {
              caption: intl.formatMessage({
                id: 'visualAid.downloading',
              }),
              showLoader: true,
            },
            notification: {},
          })

          let res = await getRequest(
            `/api/visual_aid/download-visual-aid/${record.id}/?vehicle=${vehicle}`
          )
          if (res?.status) {
            navDispatch({
              type: 'notification',
              data: {
                notification_type: 'success',
                msg: intl.formatMessage({
                  id: 'visualAid.downloadSuccess',
                }),
              },
            })
            // Awaited: the success toast above used to fire before the file was
            // even requested, so a failed download still read as a success.
            const saved = await downloadFile(
              res?.data?.visual_aid_file,
              `${record?.visual_aid_file_name}.svg`
            )
            if (!saved) {
              notifyError(navDispatch, intl.formatMessage({ id: 'visualAid.downloadError' }))
            }
          } else {
            navDispatch({
              type: 'notification',
              data: {
                notification_type: 'error',
                msg: intl.formatMessage({ id: 'visualAid.downloadError' }),
              },
            })
          }
        },
        maskClosable: true,
      })
    },
    [modal, intl, navDispatch, vehicle]
  )

  const downloadVisualAidPackage = useCallback(async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'visualAid.downloadingPackage',
        }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/visual_aid/download-visual-aid-package/?vehicle=${vehicle}`,
      true,
      true
    )

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // `res?.msg` is redundant: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'visualAid.downloadError' }),
        },
      })
    } else {
      downloadFileBlob(res, `visual-aid-${vehicle}.zip`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: 'visualAid.downloadSuccess',
          }),
        },
      })
    }
  }, [intl, navDispatch, vehicle])

  const dataSource = useMemo(() => {
    if (!state?.data?.results?.length) return []
    return state.data.results.map((item, index) => ({
      ...item,
      key: `va-${index}`,
      rowKey: `va-${item.id}`,
      index,
    }))
  }, [state.data])
  // console.log(pagination)

  return (
    <>
      {contextHolder}

      <VisualAidFormModal getVisualAidList={getVisualAidList} />

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex gap={16} wrap={false} className="full-width" align="center">
              <InputText
                name="search_visual_aid"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'visualAid.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-visual-aid"
                onChange={(value) => setSearch(value)}
              />

              <Space
                size="middle"
                align="center"
                style={{ marginLeft: 'auto' }}
              >
                <Button
                  icon={<DownloadOutlined />}
                  onClick={downloadVisualAidPackage}
                >
                  {intl.formatMessage({
                    id: 'visualAid.downloadPackage',
                  })}
                </Button>

                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    dispatch({
                      type: SHOW_HIDE_VISUAL_AID_FORM,
                      data: true,
                    })
                  }}
                >
                  {intl.formatMessage({
                    id: 'visualAid.createNew',
                  })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex vertical gap={16} className="full-width">
            <VisualAidTable
              dataSource={dataSource}
              pagination={pagination}
              getVisualAidList={getVisualAidList}
              onEdit={onEdit}
              onDelete={confirmDelete}
              onDownloadFile={downloadVisualAidFile}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default VisualAidList
